import { css, Interpolation, Theme } from '@emotion/react'

export const wrapper: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const analyticsCard: Interpolation<Theme> = (theme) => css`
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 24px;
  border-radius: 14px;
  border: 1px solid ${theme.backgrounds.backgroundOne};
  box-shadow: ${theme.boxShadows.small};
  padding: 23px;

  ${theme.breakpoints.down(850)} {
    grid-template-columns: 1fr;
  }
`

export const chartColumn: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const chartWrapper: Interpolation<Theme> = css`
  position: relative;
  height: 220px;
  margin: 0 auto;
`

export const centerText: Interpolation<Theme> = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 120px;
`

export const totalValue: Interpolation<Theme> = (theme) => css`
  color: ${theme.colors.typographyOne};
  font-size: 30px;
  font-weight: 700;
  line-height: 34px;
`

export const totalLabel: Interpolation<Theme> = (theme) => css`
  color: ${theme.colors.typographyTwo};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

export const emptyChart: Interpolation<Theme> = (theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  color: ${theme.typographyColors.typographyFour};
  text-align: center;
`

export const reasonsList: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
`

export const reasonRow: Interpolation<Theme> = css`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
`

export const reasonName: Interpolation<Theme> = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

export const reasonText: Interpolation<Theme> = (theme) => css`
  color: ${theme.typographyColors.typographyOne};
  font-size: 14px;
  font-weight: 500;
  overflow-wrap: anywhere;
`

export const reasonCount: Interpolation<Theme> = (theme) => css`
  color: ${theme.typographyColors.typographyTwo};
  font-size: 14px;
  font-weight: 700;
`

export const reasonPercent: Interpolation<Theme> = (theme) => css`
  min-width: 52px;
  text-align: right;
  color: ${theme.typographyColors.typographyThree};
  font-size: 14px;
  font-weight: 500;
`

export const dotWrapper: Interpolation<Theme> = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex: 0 0 auto;
`

export const dot = (color: string, hovered: boolean): Interpolation<Theme> => css`
  width: ${hovered ? 12 : 7}px;
  height: ${hovered ? 12 : 7}px;
  border-radius: 50%;
  background-color: ${color};
  transition: width 200ms ease-in-out, height 200ms ease-in-out;
`

export const tableSection: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const tableHeader: Interpolation<Theme> = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const tableFilters: Interpolation<Theme> = css`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

export const filterSelect: Interpolation<Theme> = css`
  min-width: 260px;
`

export const search: Interpolation<Theme> = css`
  width: 320px;
`

export const fullWidthForMobile: Interpolation<Theme> = (theme) => css`
  ${theme.breakpoints.down(650)} {
    width: 100%;
  }
`

export const tableFooter: Interpolation<Theme> = (theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: -4px;

  ${theme.breakpoints.down(760)} {
    width: 100%;
    align-items: flex-end;
    flex-direction: column-reverse;
  }
`

export const messageText: Interpolation<Theme> = (theme) => css`
  color: ${theme.typographyColors.typographyTwo};
  font-size: 16px;
  font-weight: 500;
`

export const tableWrapper: Interpolation<Theme> = css`
  .table-row {
    min-height: 70px;
  }
`

export const sectionTabs: Interpolation<Theme> = css`
  width: auto;
  min-width: 0;
`

export const testimonialCard: Interpolation<Theme> = (theme) => css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
  background: ${theme.backgrounds.backgroundFour};
  border: 1px solid ${theme.backgrounds.backgroundOne};
  box-shadow: ${theme.boxShadows.small};
  border-radius: 14px;
`

export const testimonialIdentity: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const testimonialName: Interpolation<Theme> = (theme) => css`
  color: ${theme.typographyColors.typographyOne};
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  overflow-wrap: anywhere;
`

export const testimonialEmail: Interpolation<Theme> = (theme) => css`
  color: ${theme.typographyColors.typographyThree};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  overflow-wrap: anywhere;
`

export const testimonialBody: Interpolation<Theme> = (theme) => css`
  margin: 0;
  padding: 14px;
  background: ${theme.backgrounds.backgroundFive};
  border-radius: 12px;
  color: ${theme.typographyColors.typographyTwo};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

export const statusCard: Interpolation<Theme> = (theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.backgrounds.backgroundFour};
  border: 1px solid ${theme.backgrounds.backgroundOne};
  box-shadow: ${theme.boxShadows.small};
  border-radius: 14px;
  min-height: 70px;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: ${theme.typographyColors.typographyTwo};
  font-family: ${theme.fonts.family.PlusJakartaSans};
`

export const statusLoader: Interpolation<Theme> = css`
  min-height: 200px;
`
